import { MutationCache, QueryClient, keepPreviousData, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Paginated } from '@denwen-msp/shared';
import { ApiError, api, errorMessage, type ApiOptions } from './api';

/**
 * Queries that summarise records of many modules: any successful write anywhere marks them
 * stale. One on screen refetches at once; the others when next shown. They are the organization
 * overview's counts and the Organizations list, whose rows count each organization's tickets,
 * configurations and contacts (P2-0, BUG-43).
 */
export const isSummaryKey = (key: QueryKey) => key[0] === 'organizations' && (key[2] === 'overview' || key[1] === 'list');
export const refreshSummaries = (client: QueryClient) => client.invalidateQueries({ predicate: (q) => isSummaryKey(q.queryKey) });

/** The app's query client (a function so tests get a fresh one that behaves the same). */
export function createQueryClient() {
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      onSuccess: () => refreshSummaries(client),
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      },
      mutations: {
        // Mutations show their own toast unless they handle errors themselves (forms map fields).
        onError: (err) => {
          if (err instanceof ApiError && err.fields && Object.keys(err.fields).length) return;
          toast.error(errorMessage(err));
        },
      },
    },
  });
  return client;
}

export const queryClient = createQueryClient();

/** GET with caching. `useApi<Org>(['organizations', id], `/organizations/${id}`)` */
export function useApi<T>(key: QueryKey, path: string, opts: { query?: ApiOptions['query']; enabled?: boolean; staleTime?: number } = {}) {
  return useQuery({
    queryKey: [...key, opts.query ?? {}],
    queryFn: ({ signal }) => api<T>(path, { query: opts.query, signal }),
    enabled: opts.enabled ?? true,
    ...(opts.staleTime !== undefined ? { staleTime: opts.staleTime } : {}),
  });
}

/** Paginated list; keeps the previous page on screen while the next one loads. */
export function useList<T>(key: QueryKey, path: string, query: ApiOptions['query'] = {}, enabled = true) {
  return useQuery({
    queryKey: [...key, 'list', query],
    queryFn: ({ signal }) => api<Paginated<T>>(path, { query, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * Write call that refreshes the given query keys afterwards.
 * `const save = useApiMutation((body) => api('/x', { method: 'POST', body }), [['x']], 'Saved')`
 */
export function useApiMutation<TVars, TResult = unknown>(fn: (vars: TVars) => Promise<TResult>, invalidate: QueryKey[] = [], successMessage?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => qc.invalidateQueries({ queryKey: key })));
      if (successMessage) toast.success(successMessage);
    },
  });
}
